/**
 * React Query hooks for capacity data.
 */

import { useQuery } from '@tanstack/react-query';
import { format, addDays, startOfWeek, endOfWeek, parseISO } from 'date-fns';
import { api } from '../lib/api';

/** Skill capacity breakdown */
export interface SkillCapacity {
  skillId: string;
  skillName: string;
  availableMinutes: number;
  demandedMinutes: number;
  demandedMinutesMin: number;
  demandedMinutesMax: number;
}

/** Period capacity data from GET /api/v1/dashboards/capacity */
export interface CapacityPeriod {
  start: string;
  end: string;
  availableMinutes: number;
  demandedMinutes: number;
  utilizationPercent: number;
  bySkill: SkillCapacity[];
}

/** Raw API response shape (different field names from our CapacityPeriod) */
interface ApiCapacityPeriod {
  periodStart: string;
  periodEnd: string;
  bySkill: SkillCapacity[];
}

interface CapacityResponse {
  data: {
    granularity: string;
    periods: ApiCapacityPeriod[];
  };
}

export type Granularity = 'day' | 'week' | 'month';

interface UseCapacityOptions {
  startDate?: string;
  endDate?: string;
  granularity?: Granularity;
  skillId?: string;
}

const WEEK_COUNT = 13;

/**
 * Get default date range (current week + 12 weeks ahead = 13 weeks total)
 */
function getDefaultDateRange() {
  const today = new Date();
  const start = startOfWeek(today, { weekStartsOn: 1 }); // Monday
  const end = endOfWeek(addDays(start, (WEEK_COUNT - 1) * 7), { weekStartsOn: 1 }); // 13 weeks total

  return {
    startDate: format(start, 'yyyy-MM-dd'),
    endDate: format(end, 'yyyy-MM-dd'),
  };
}

/**
 * Pad periods to always include all 13 weeks.
 * Weeks without API data carry forward the last known capacity per skill,
 * since workforce availability doesn't drop to zero just because there's no demand.
 */
function padToWeeks(periods: CapacityPeriod[], startDate: string): CapacityPeriod[] {
  const start = parseISO(startDate);

  // Generate all 13 Monday start dates
  const weekStarts = Array.from({ length: WEEK_COUNT }, (_, i) =>
    format(addDays(start, i * 7), 'yyyy-MM-dd')
  );

  // Collect all unique skills across all periods
  const skillMap = new Map<string, string>();
  for (const period of periods) {
    for (const skill of period.bySkill) {
      if (!skillMap.has(skill.skillId)) {
        skillMap.set(skill.skillId, skill.skillName);
      }
    }
  }

  // Index existing periods by start date
  const periodMap = new Map(periods.map((p) => [p.start, p]));

  // Build padded array, then carry forward capacity
  const padded = weekStarts.map((weekStart) => {
    const existing = periodMap.get(weekStart);
    if (existing) {
      // Ensure all skills are present (some periods may be missing skills with no data)
      const existingSkillIds = new Set(existing.bySkill.map((s) => s.skillId));
      const paddedSkills = [...existing.bySkill];
      for (const [skillId, skillName] of skillMap) {
        if (!existingSkillIds.has(skillId)) {
          paddedSkills.push({ skillId, skillName, availableMinutes: 0, demandedMinutes: 0, demandedMinutesMin: 0, demandedMinutesMax: 0 });
        }
      }
      return { ...existing, bySkill: paddedSkills };
    }

    const weekEnd = format(endOfWeek(parseISO(weekStart), { weekStartsOn: 1 }), 'yyyy-MM-dd');
    return {
      start: weekStart,
      end: weekEnd,
      availableMinutes: 0,
      demandedMinutes: 0,
      utilizationPercent: 0,
      bySkill: Array.from(skillMap, ([skillId, skillName]) => ({
        skillId,
        skillName,
        availableMinutes: 0,
        demandedMinutes: 0,
        demandedMinutesMin: 0,
        demandedMinutesMax: 0,
      })),
    };
  });

  // Carry forward capacity: weeks without API data inherit the last known
  // capacity per skill (workforce doesn't disappear when demand is zero)
  const lastCapacity = new Map<string, number>();
  for (const period of padded) {
    for (const skill of period.bySkill) {
      if (skill.availableMinutes > 0) {
        lastCapacity.set(skill.skillId, skill.availableMinutes);
      } else if (lastCapacity.has(skill.skillId)) {
        skill.availableMinutes = lastCapacity.get(skill.skillId)!;
      }
    }
    // Recalculate period-level totals after carry-forward
    period.availableMinutes = period.bySkill.reduce((sum, s) => sum + s.availableMinutes, 0);
    period.utilizationPercent = period.availableMinutes > 0
      ? Math.round((period.demandedMinutes / period.availableMinutes) * 100)
      : 0;
  }

  return padded;
}

/**
 * Generate mock capacity data for development when API is unavailable.
 */
function generateMockData(granularity: Granularity): { periods: CapacityPeriod[] } {
  const skills = [
    { skillId: 'skill-1', skillName: 'Software Development' },
    { skillId: 'skill-2', skillName: 'Electrical Assembly' },
    { skillId: 'skill-3', skillName: 'Mechanical Engineering' },
    { skillId: 'skill-4', skillName: 'Testing/QC' },
    { skillId: 'skill-5', skillName: 'Final Assembly' },
  ];

  const periodCount = granularity === 'day' ? 14 : granularity === 'week' ? 4 : 3;
  const today = new Date();

  return {
    periods: Array.from({ length: periodCount }, (_, i) => {
      const start = new Date(today);
      if (granularity === 'day') {
        start.setDate(start.getDate() + i);
      } else if (granularity === 'week') {
        start.setDate(start.getDate() + i * 7);
      } else {
        start.setMonth(start.getMonth() + i);
      }

      const availableMinutes = 24000 + Math.floor(Math.random() * 4000);
      const utilizationBase = 65 + Math.floor(Math.random() * 40); // 65-105%
      const demandedMinutes = Math.floor(availableMinutes * (utilizationBase / 100));

      return {
        start: format(start, 'yyyy-MM-dd'),
        end: format(start, 'yyyy-MM-dd'),
        availableMinutes,
        demandedMinutes,
        utilizationPercent: utilizationBase,
        bySkill: skills.map((skill) => {
          const skillAvail = Math.floor(availableMinutes / skills.length) + Math.floor(Math.random() * 1000);
          const skillUtil = 50 + Math.floor(Math.random() * 60); // 50-110%
          const demanded = Math.floor(skillAvail * (skillUtil / 100));
          return {
            ...skill,
            availableMinutes: skillAvail,
            demandedMinutes: demanded,
            demandedMinutesMin: Math.round(demanded * 0.8),
            demandedMinutesMax: Math.round(demanded * 1.2),
          };
        }),
      };
    }),
  };
}

/**
 * Fetch capacity utilization data.
 * Falls back to mock data in development if API is unavailable.
 */
export function useCapacity(options: UseCapacityOptions = {}) {
  const defaults = getDefaultDateRange();
  const {
    startDate = defaults.startDate,
    endDate = defaults.endDate,
    granularity = 'week',
    skillId,
  } = options;

  return useQuery({
    queryKey: ['capacity', { startDate, endDate, granularity, skillId }],
    queryFn: async () => {
      const response = await api.get<CapacityResponse>('/dashboards/capacity', {
        params: { startDate, endDate, granularity, skillId },
      });

      // Transform API response to match CapacityPeriod shape
      // API returns periodStart/periodEnd and per-skill data only;
      // we need start/end and aggregate totals at the period level
      const rawPeriods = response.data?.periods ?? [];
      const apiPeriods: CapacityPeriod[] = rawPeriods.map((p) => {
        const bySkill: SkillCapacity[] = (p.bySkill ?? []).map((s) => ({
          ...s,
          demandedMinutesMin: s.demandedMinutesMin ?? 0,
          demandedMinutesMax: s.demandedMinutesMax ?? 0,
        }));
        const totalAvailable = bySkill.reduce((sum, s) => sum + (s.availableMinutes || 0), 0);
        const totalDemanded = bySkill.reduce((sum, s) => sum + (s.demandedMinutes || 0), 0);
        const utilPercent = totalAvailable > 0 ? Math.round((totalDemanded / totalAvailable) * 100) : 0;

        return {
          start: p.periodStart,
          end: p.periodEnd,
          availableMinutes: totalAvailable,
          demandedMinutes: totalDemanded,
          utilizationPercent: utilPercent,
          bySkill,
        };
      });

      // Pad to always have 13 weeks
      const periods = granularity === 'week'
        ? padToWeeks(apiPeriods, startDate)
        : apiPeriods;

      return { periods };
    },
    staleTime: 60_000, // 1 minute
    refetchInterval: 120_000, // Refetch every 2 minutes
    refetchOnWindowFocus: false, // Prevent duplicate calls on tab switch
  });
}

/**
 * Calculate utilization status based on percentage.
 */
export function getUtilizationStatus(percent: number): 'low' | 'optimal' | 'high' | 'overloaded' {
  if (percent < 50) return 'low';
  if (percent <= 85) return 'optimal';
  if (percent <= 100) return 'high';
  return 'overloaded';
}

/**
 * Format minutes as hours for display.
 */
export function formatMinutesAsHours(minutes: number): string {
  const hours = minutes / 60;
  if (hours >= 1000) {
    return `${(hours / 1000).toFixed(1)}k hrs`;
  }
  return `${hours.toFixed(0)} hrs`;
}
